/**
 * Ops Dashboard App — layout + hash router
 */
import React, { useState, useEffect, useCallback } from "react";
import { getCurrentRoute, navigate, type OpsRoute } from "./router.js";
import { isLoggedIn, clearSession, auth, getRole } from "./api.js";
import { LoginPage } from "./LoginPage.js";
import { OverviewView }       from "./views/OverviewView.js";
import { TenantsView }        from "./views/TenantsView.js";
import { SubscriptionsView }  from "./views/SubscriptionsView.js";
import { PublishingView }     from "./views/PublishingView.js";
import { WorkersView }        from "./views/WorkersView.js";
import { MetricsView }        from "./views/MetricsView.js";
import { SearchView }         from "./views/SearchView.js";
import { AuditLogView }       from "./views/AuditLogView.js";
import { ProvidersView }      from "./views/ProvidersView.js";
import { NotificationsView }  from "./views/NotificationsView.js";
import { useAsync }           from "./hooks.js";
import { notifications as notifApi } from "./api.js";

interface NavItem { route: OpsRoute; label: string; icon: string; }

const NAV_ITEMS: NavItem[] = [
  { route: "overview",       label: "Overview",        icon: "🏠" },
  { route: "tenants",        label: "Tenants",         icon: "🏢" },
  { route: "subscriptions",  label: "Subscriptions",   icon: "💳" },
  { route: "publishing",     label: "Publishing",      icon: "🌐" },
  { route: "workers",        label: "Workers",         icon: "⚙️" },
  { route: "metrics",        label: "Metrics",         icon: "📊" },
  { route: "providers",      label: "AI Providers",    icon: "🤖" },
  { route: "search",         label: "Search",          icon: "🔍" },
  { route: "audit-log",      label: "Audit Log",       icon: "📋" },
  { route: "notifications",  label: "Notifications",   icon: "🔔" },
];

function RouteView({ route }: { route: OpsRoute }) {
  switch (route) {
    case "overview":      return <OverviewView />;
    case "tenants":       return <TenantsView />;
    case "subscriptions": return <SubscriptionsView />;
    case "publishing":    return <PublishingView />;
    case "workers":       return <WorkersView />;
    case "metrics":       return <MetricsView />;
    case "providers":     return <ProvidersView />;
    case "search":        return <SearchView />;
    case "audit-log":     return <AuditLogView />;
    case "notifications": return <NotificationsView />;
    default:              return <OverviewView />;
  }
}

export function App() {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn);
  const [route, setRoute]       = useState<OpsRoute>(getCurrentRoute);

  // Track unread notification count for badge
  const unreadFn = useCallback(() => loggedIn ? notifApi.unreadCount() : Promise.resolve({ count: 0 }), [loggedIn]);
  const { data: unreadData } = useAsync(unreadFn, [loggedIn]);
  const unreadCount = (unreadData as any)?.count ?? 0;

  // Hash routing
  useEffect(() => {
    const handler = () => setRoute(getCurrentRoute());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  async function handleLogout() {
    await auth.logout().catch(() => {});
    clearSession();
    setLoggedIn(false);
  }

  if (!loggedIn) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div className="ops-shell">
      {/* Sidebar */}
      <aside className="ops-sidebar">
        <div className="ops-sidebar-logo">
          <span>Bizzness</span> Ops
        </div>
        <nav className="ops-nav">
          {NAV_ITEMS.map(item => (
            <button
              key={item.route}
              className={`ops-nav-item${route === item.route ? " active" : ""}`}
              onClick={() => navigate(item.route)}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
              {item.route === "notifications" && unreadCount > 0 && (
                <span className="ops-notif-dot">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="ops-sidebar-footer">
          <span style={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{getRole()}</span>
          <button
            style={{ background: "none", border: "none", color: "var(--ops-muted)",
              cursor: "pointer", fontSize: "0.75rem" }}
            onClick={handleLogout}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ops-content">
        <RouteView route={route} />
      </main>
    </div>
  );
}
